import { useLocation, useNavigate } from "react-router-dom";
import { FiMail, FiPhone, FiArrowLeft, FiMapPin } from "react-icons/fi";
import { TopNavBarComponent } from "../../components/navbar/TopNavBarComponent";
import { ProductItem } from "../../components/cards/ProductItem";
import { ProductInterface } from "../OrderDetailsPage/OrderDetailsInterface";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { formatDateInZone, formatCurrency, getCurrencySymbol } from "../../utils/formatters";

interface AbandonedCartProduct {
  name: string;
  images: string[];
  units: number;
  unit_price: number;
  unit_price_with_discount: number;
  total_value_with_discount: number;
  product_id: string;
}

interface CustomerData {
  name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address?: string | null;
  address_detail?: string | null;
  city?: string | null;
  department?: string | null;
}

interface AbandonedCart {
  session_id: string;
  website_id: string;
  country_code?: string | null;
  currency_code?: string | null;
  last_stage: string;
  customer_data: CustomerData;
  first_seen_at: string;
  last_seen_at: string;
  products?: AbandonedCartProduct[];
}

const LAST_STAGE_LABEL_KEY: Record<string, string> = {
  addCheckoutInfo:  "abandoned-stage-checkoutForm",
  addPaymentInfo:   "abandoned-stage-paymentModal",
};

export const AbandonedCartDetailPage = () => {
  const { language, timezone, country } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();

  const cart = state?.cart as AbandonedCart | undefined;
  const funnelName = state?.funnelName as string | undefined;
  const currencySymbol = getCurrencySymbol(cart?.currency_code || cart?.country_code || country);

  if (!cart) {
    return (
      <div>
        <TopNavBarComponent />
        <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500">
          <p className="text-lg font-medium mb-4">Carrito no encontrado</p>
          <button
            onClick={() => navigate("/orders")}
            className="text-sm text-blue-500 hover:underline"
          >
            Volver a Órdenes
          </button>
        </div>
      </div>
    );
  }

  const products = cart.products ?? [];
  const total = products.reduce((sum, p) => sum + p.total_value_with_discount, 0);

  const fullName = [cart.customer_data.name, cart.customer_data.last_name].filter(Boolean).join(" ") || null;

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((p) => p.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <div>
      <TopNavBarComponent />

      <div className="px-6 pt-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
        >
          <FiArrowLeft size={14} />
          Volver
        </button>
      </div>

      <div className="flex flex-col md:flex-row px-6 pb-8 gap-6">
        {/* Left column — Products */}
        <div className="md:w-[65%]">
          <div className="border rounded-lg overflow-hidden bg-white mb-6">
            <div className="p-4 flex items-center gap-3 border-b border-gray-100">
              <div className="h-5 w-5 rounded-full bg-orange-200 flex-shrink-0"></div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                  {dictionary["last-stage-reached"]?.[language] || "Última etapa"}
                </p>
                <h2 className="font-medium text-gray-900">
                  {dictionary[LAST_STAGE_LABEL_KEY[cart.last_stage]]?.[language] ?? cart.last_stage}
                </h2>
              </div>
            </div>

            {products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <p className="text-sm">Sin información de productos disponible</p>
              </div>
            ) : (
              <div>
                {products.map((product, i) => (
                  <ProductItem
                    key={i}
                    product={{
                      id: i,
                      order_id: 0,
                      product_id: product.product_id,
                      name: product.name,
                      description: "",
                      images: product.images,
                      units: product.units,
                      unit_price: product.unit_price,
                      unit_price_with_discount: product.unit_price_with_discount,
                      total_value: product.unit_price * product.units,
                      total_value_with_discount: product.total_value_with_discount,
                      created_at: "",
                      updated_at: "",
                    } as ProductInterface}
                    currencySymbol={currencySymbol}
                  />
                ))}

                {/* Total */}
                <div className="flex justify-between items-center px-4 py-4 border-t border-gray-100 mx-4 mb-4">
                  <span className="text-sm font-medium text-gray-700">Total</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(total, currencySymbol)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column — Customer & Cart info */}
        <div className="md:w-[35%] space-y-4">
          {/* Customer card */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4">{dictionary["customer"]?.[language] || "Cliente"}</h2>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                {getInitials(fullName)}
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="font-medium text-gray-900">
                  {fullName || <span className="text-gray-400">Sin nombre</span>}
                </h3>
                {cart.customer_data.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiMail className="text-gray-400 flex-shrink-0" />
                    <span>{cart.customer_data.email}</span>
                  </div>
                )}
                {cart.customer_data.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiPhone className="text-gray-400 flex-shrink-0" />
                    <span>{cart.customer_data.phone}</span>
                  </div>
                )}
                {(cart.customer_data.address || cart.customer_data.city || cart.customer_data.department) && (
                  <div className="flex items-start gap-2 text-sm text-gray-600 mt-1">
                    <FiMapPin className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>
                      {[
                        cart.customer_data.address,
                        cart.customer_data.address_detail,
                        cart.customer_data.city,
                        cart.customer_data.department,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cart info card */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Detalle del carrito</h2>
            <div className="space-y-3 text-sm">
              {funnelName && (
                <InfoRow label={dictionary["funnel"]?.[language] || "Embudo"} value={funnelName} />
              )}
              <InfoRow
                label={dictionary["last-stage-reached"]?.[language] || "Última etapa"}
                value={
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                    {dictionary[LAST_STAGE_LABEL_KEY[cart.last_stage]]?.[language] ?? cart.last_stage}
                  </span>
                }
              />
              <InfoRow label="Primera visita" value={formatDateInZone(cart.first_seen_at, timezone)} />
              <InfoRow
                label={dictionary["last-activity"]?.[language] || "Última actividad"}
                value={formatDateInZone(cart.last_seen_at, timezone)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-gray-400 shrink-0">{label}</span>
    <span className="text-gray-700 text-right">{value}</span>
  </div>
);
